import { Button, SectionTable } from 'components';
import {
    useCurrentCountry,
    useEndDate,
    useRootCategories,
    usePlan,
} from 'utils/hooks';
import { useEffect, useState } from 'react';
import { ROOT_NAMES } from '@/config/constants';
import { buildExportFilename } from 'utils/formatCSV';
import { useAffinity, useCatFives } from 'utils/hooks/api';

const ROOT_NAME_WITHOUT_AUDIENCE = ROOT_NAMES.filter(
    (name) => !['audience', 'ingredients'].includes(name),
);

function AffinityCategoriesTable({
    affinityId,
    categoryIds,
    countryId,
    controller,
    icon,
    title,
    subtitle,
    tooltipKey,
    tooltipExample,
}) {
    const affinityCategories = new Set(categoryIds);
    const country = useCurrentCountry();
    const date = useEndDate('google');
    const {
        isLoading: isLoadingPlan,
        industry,
        error: errorPlan,
        refetch,
    } = usePlan();
    const rootCategoriesQuery = useRootCategories();
    const rootCategoryIds = (rootCategoriesQuery.data || []).reduce(
        (acc, { name, id }) => {
            if (
                ROOT_NAME_WITHOUT_AUDIENCE.includes(name) &&
                affinityCategories.has(id)
            )
                acc.push(id);
            return acc;
        },
        [],
    );
    const affinityQuery = useAffinity({ id: affinityId });
    const affinity = affinityQuery.data;
    const affinityName =
        affinity && affinity.name ? affinity.name : 'related search';
    const [parentIds, setParentIds] = useState([]);
    const parentNode = parentIds.length
        ? parentIds[parentIds.length - 1]
        : null;
    const isParentBlocked =
        parentNode &&
        parentNode.depth === 'cat1' &&
        industry &&
        !industry.includes(parentNode.name);
    const isCatFiveMode =
        parentNode && parentNode.depth === 'cat1'
            ? parentNode.children && parentNode.children.length === 1
            : parentNode && parentNode.depth;

    useEffect(() => setParentIds([]), [affinityId]);

    const parentChildrenIds =
        parentNode && parentNode.children
            ? parentNode.children.filter((id) => affinityCategories.has(id))
            : null;

    const {
        data: catFiveIds,
        isLoading: isLoadingCatFiveIds,
        isFetching: isFetchingCatFiveIds,
    } = useCatFives({
        enabled: !!(isCatFiveMode && parentNode),
        rootIds: parentNode ? [parentNode.id] : null,
        allowList: categoryIds,
    });

    const fetchingCategories =
        affinityQuery.isLoading ||
        isLoadingPlan ||
        isLoadingCatFiveIds ||
        isFetchingCatFiveIds;

    const tableCategoryIds = !parentNode
        ? rootCategoryIds
        : isParentBlocked || errorPlan
          ? []
          : isCatFiveMode
            ? catFiveIds
            : parentChildrenIds;

    return (
        <SectionTable
            source="google"
            icon={icon}
            title={title}
            subtitle={subtitle}
            hideCount={true}
            tooltipKey={tooltipKey}
            tooltipExample={tooltipExample}
            paginateBy="categories"
            affinities={affinityId}
            categories={
                tableCategoryIds && tableCategoryIds.length
                    ? tableCategoryIds
                    : fetchingCategories
                      ? null
                      : tableCategoryIds
            }
            countries={countryId}
            parentNode={parentNode}
            onParentNodeClick={(e) =>
                setParentIds(parentIds.slice(0, parentIds.length - 1))
            }
            {...(!isCatFiveMode && {
                onRowClick: (e, node) => setParentIds([...parentIds, node]),
            })}
            name={affinityName}
            filename={buildExportFilename({
                date,
                country,
                source: 'google',
                entity: parentNode
                    ? `${affinityName} ${parentNode.name}`
                    : affinityName,
                type: 'industry',
                kind: 'data',
            })}
            {...(isParentBlocked && {
                noDataMessage:
                    'Contact a Spate representative for access to this vertical.',
            })}
            {...(errorPlan && {
                noDataMessage: (
                    <span>
                        Unable to get plan type.{' '}
                        <Button onClick={refetch} intent="secondary">
                            Retry
                        </Button>
                    </span>
                ),
            })}
        />
    );
}

export default AffinityCategoriesTable;
